import { useState } from 'react';
import { supabase } from '../supabase';
import { COLORS, RADIUS } from '../theme';

// Two-step OTP entry. Cloudflare Access sits in front of this page, so anyone
// who reaches the login form has already cleared Access — the Supabase login
// is a second, independent factor that also decides admin identity.
//
// Flow:
//   1. Founder enters email → signInWithOtp() with shouldCreateUser:false
//   2. Founder enters the 8-digit code from email → verifyOtp({ type:'email' })
// Admin gate (server-side is_admin RPC) runs after auth via admin-check.

type Status = 'idle' | 'sending' | 'sent' | 'verifying' | 'error';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sentOrVerifying = status === 'sent' || status === 'verifying';

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    setErrorMessage(null);
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false },
    });
    if (error) {
      setStatus('error');
      setErrorMessage(error.message);
      return;
    }
    setStatus('sent');
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (code.length < 6) return;
    setStatus('verifying');
    setErrorMessage(null);
    const { error } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: code,
      type: 'email',
    });
    if (error) {
      setStatus('error');
      setErrorMessage(error.message);
      return;
    }
    // On success the session listener in useSession() picks up the new
    // session and unmounts this screen — no explicit navigation needed.
  }

  function useDifferentEmail() {
    setStatus('idle');
    setCode('');
    setErrorMessage(null);
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <span style={styles.brandKicker}>REVLOOM</span>
          <span style={styles.brandStamp}>ops</span>
        </div>
        <h1 style={styles.title}>Founder Ops</h1>
        <p style={styles.subtitle}>
          Sign in with the founder email. Non-admin accounts will be rejected server-side.
        </p>

        {!sentOrVerifying ? (
          <form onSubmit={requestCode}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="you@revloomapp.com"
              disabled={status === 'sending'}
            />
            <button type="submit" style={styles.button} disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send sign-in code'}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <label style={styles.label}>One-time code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              style={{ ...styles.input, letterSpacing: 6, fontVariantNumeric: 'tabular-nums' }}
              placeholder="12345678"
              disabled={status === 'verifying'}
            />
            <button
              type="submit"
              style={styles.button}
              disabled={status === 'verifying' || code.length < 6}
            >
              {status === 'verifying' ? 'Verifying…' : 'Verify code'}
            </button>
            <button
              type="button"
              onClick={useDifferentEmail}
              style={styles.secondaryButton}
              disabled={status === 'verifying'}
            >
              Use a different email
            </button>
            <p style={styles.hint}>
              Enter the one-time code sent to <b>{email}</b>. The code is 8 digits and expires shortly.
            </p>
          </form>
        )}

        {status === 'error' && errorMessage && (
          <p style={styles.error}>{errorMessage}</p>
        )}

        <div style={styles.footer}>
          Protected by Cloudflare Access · No customer data on this surface.
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center',
    background: `radial-gradient(circle at 30% 20%, #17233A 0%, #0A0B0D 60%)`,
    padding: 24,
  },
  card: {
    width: 420, background: COLORS.surface, borderRadius: RADIUS.card,
    border: `1px solid ${COLORS.border}`, padding: 32,
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  brandRow: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 24 },
  brandKicker: { letterSpacing: 3, fontSize: 11, color: COLORS.textMuted, fontWeight: 600 },
  brandStamp: {
    fontSize: 10, background: COLORS.accent, color: '#0A0B0D',
    padding: '2px 6px', borderRadius: 4, fontWeight: 700,
  },
  title: { fontSize: 28, margin: 0, marginBottom: 8, color: COLORS.text },
  subtitle: { color: COLORS.textSecondary, fontSize: 13, marginTop: 0, marginBottom: 24, lineHeight: 1.5 },
  label: { display: 'block', fontSize: 11, color: COLORS.textMuted, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 },
  input: {
    width: '100%', background: COLORS.surfaceAlt, color: COLORS.text,
    border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.input,
    padding: '12px 14px', fontSize: 14, marginBottom: 16,
  },
  button: {
    width: '100%', background: COLORS.accent, color: '#0A0B0D',
    border: 'none', borderRadius: RADIUS.input, padding: '12px 16px',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  secondaryButton: {
    width: '100%', background: 'transparent', color: COLORS.textSecondary,
    border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.input,
    padding: '10px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    marginTop: 10,
  },
  hint: { color: COLORS.textSecondary, fontSize: 13, marginTop: 16, lineHeight: 1.5 },
  error: { color: COLORS.critical, fontSize: 13, marginTop: 16 },
  footer: {
    marginTop: 24, borderTop: `1px solid ${COLORS.border}`, paddingTop: 16,
    fontSize: 11, color: COLORS.textMuted, textAlign: 'center' as const,
  },
};
